import { useEffect, useState } from 'react';

import type { Option } from '@eluve/components';
import { formatHumanName } from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

const NO_CACHE = { cache: 'no-cache' } as const;
/** Upper bound on assignee options; tenants have far fewer practitioners. */
const PRACTITIONER_LIMIT = 200;

/**
 * Assignee facet options — the tenant's Practitioners, browser-direct from
 * Medplum. Separate from the page fetch because server pagination means the
 * loaded page can't enumerate every possible assignee. A scoped viewer sees
 * only the practitioners their access policy permits (often just themselves),
 * which is the correct, if short, list.
 */
export function useClinicalTaskAssigneeOptions(): Option[] {
  const { client, enabled } = useMedplum();
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (!enabled || !client) return;
    let cancelled = false;
    void (async () => {
      try {
        const practitioners = await client.searchResources(
          'Practitioner',
          { _count: PRACTITIONER_LIMIT, _sort: 'family' },
          NO_CACHE,
        );
        if (cancelled) return;
        setOptions(
          practitioners.flatMap((p) => {
            const label = formatHumanName(p.name?.[0]);
            return p.id && label
              ? [{ label, value: `Practitioner/${p.id}` }]
              : [];
          }),
        );
      } catch {
        if (!cancelled) setOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, enabled]);

  return options;
}
